import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CTA from 'Src/components/buttons/CTA';
import { IconCTA } from 'Src/components/buttons/IconCTA';
import Copy from 'Src/components/Copy';
import FontIcon from 'Src/components/icons/FontIcon';
import { Flex } from 'Src/components/layout/Flex';
import { useCurrentCheck } from 'Src/feature/check/useCurrentCheck';
import { useFloorPlan } from 'Src/feature/floor-plan/useFloorPlan';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useResizeObserver } from 'Src/feature/resize/useResizeObserver';

import styles from './CheckSplitBySeat.module.scss';

const { Provider: FormProvider  } = getTypedFormComponents<FormModel>();

const SEAT = {
  width: 60,
  height: 60,
};

class FormModel {
  seatNames: string[];
  selectedSeatNames: string[];
}

enum ChecksSplitBySeatType {
  Assign,
  CreateChecks
}

const sectionId = 'ChecksSplitBySeat';

interface ChecksSplitBySeatProps {
  seatNames: string[];
  onCancel?: () => void;
  onDone: (seatNames: string[]) => void;
  type: ChecksSplitBySeatType;
}

const ChecksSplitBySeat: FC<React.PropsWithChildren<ChecksSplitBySeatProps>> = (props) => {
  const { t } = useTranslation();

  const resize = useResizeObserver({
    onResize: ({ rect }) => {
      containerSet({ size: rect.width });
    },
  });

  const currentCheck = useCurrentCheck();
  const floorPlanCheckTable = useFloorPlan.query.checkTable(currentCheck.query.data);

  const [seatNames, seatNamesSet] = useState(props.seatNames);
  const [type, typeSet] = useState(props.type);
  const [table, tableSet] = useState(floorPlanCheckTable.data);
  const [container, containerSet] = useState({ size: 0 });

  const form = useForm<FormModel>({
    defaultValues: {
      seatNames: table?.seats?.map(({ name }) => name),
      selectedSeatNames: seatNames,
    }
  });

  const formValues = form.state.watch();

  const seats = useMemo(() => {
    const seatNames = (formValues.seatNames || []).slice().sort();
    const seatCount = seatNames.length;
    const degrees = 360;
    const degreeStart = -90;
    const degreeIncrement = degrees / seatCount;
    const origin = (container.size / 2) - (SEAT.width / 2); // center of circle
    const radiusPadding = 5;
    const radius = Math.ceil(((container.size + SEAT.width) / 2) + radiusPadding);
    return seatNames.map((name, i) => {
      const y = Math.sin((degreeStart + (degreeIncrement * i)) * (Math.PI / 180)) * radius;
      const x = Math.cos((degreeStart + (degreeIncrement * i)) * (Math.PI / 180)) * radius;
      const top = Math.ceil(y + origin);
      const left = Math.ceil(x + origin);
      return { ...SEAT, top, left, name };
    });
  }, [container.size, formValues.seatNames]);

  const selectedSeatNames = (formValues.selectedSeatNames || []).join(', ');

  const copy = {
    button: '',
    confirm: '',
    cancel: t('labels.cancel'),
    header: '',
    info: ''
  };

  if (type === ChecksSplitBySeatType.Assign) {
    copy.button = t('checkSplit.labels.assignSeatPositions');
    copy.confirm = t('checkSplit.labels.assignSeatPositions');
    copy.header = t('checkSplit.labels.selectedSeatNameAssign', { selectedSeatNames: selectedSeatNames });
    copy.info = '';
  }
  if (type === ChecksSplitBySeatType.CreateChecks) {
    copy.button = t('checkSplit.labels.fromSeatPosition');
    copy.confirm = t('checkSplit.labels.splitAndMoveToNewChecks');
    copy.header = t('checkSplit.labels.selectedSeatNameCreate', { selectedSeatNames: selectedSeatNames });
    copy.info = t('checkSplit.info.shouldNotSplitWhenAlreadySplit');
  }

  const isSelected = (seatName: string) => Boolean((formValues.selectedSeatNames || []).find((name) => name === seatName));
  const onSelect = (seatName: string) => {
    let selectedSeatNames = formValues.selectedSeatNames || [];
    if (isSelected(seatName)) {
      selectedSeatNames = selectedSeatNames.filter((name) => seatName !== name);
    } else {
      selectedSeatNames.push(seatName);
    }
    // always sort em
    selectedSeatNames = selectedSeatNames.sort();
    form.action.setValue('selectedSeatNames', selectedSeatNames);
  };

  const modal = {
    ...useModalControl(),
    onDone: () => {
      props.onDone(formValues.selectedSeatNames || []);
      modal.close();
    },
  };


  // bind incoming props to local store instance
  useEffect(() => {
    if (JSON.stringify(props.seatNames) !== JSON.stringify(seatNames)) {
      seatNamesSet(props.seatNames);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.seatNames]);

  // bind incoming props to local store instance
  useEffect(() => {
    if (type !== props.type) {
      typeSet(props.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.type]);

  // bind incoming props to local store instance
  useEffect(() => {
    if (table !== floorPlanCheckTable.data) {
      tableSet(floorPlanCheckTable.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlanCheckTable.data]);

  if (!table) {
    console.info(sectionId, 'NO TABLE', 'Table Not Found');
    return <></>;
  }

  return (
    <FormProvider usedForm={form}>
      <CTA
        data-testid={`${sectionId}-button`}
        color="primary"
        icon={type === ChecksSplitBySeatType.CreateChecks ? 'add' : undefined}
        onClick={modal.open}
        label={copy.button}
      />
      <Modal
        data-testid={`${sectionId}-modal`}
        open={modal.isOpen}
        onBackDropClick={modal.close}
      >
        <ModalHeader>
          <ModalTitle copy={copy.header} />
        </ModalHeader>

        <ModalContent>
          <Flex fullHeight fullWidth>
            {/* extra div wrapper(s) used for margin/resize/layout reasons */}
            <div>
              <div className={styles['container']}>
                <div className={styles['table']} ref={resize.setElement}>
                  <Copy value={table?.name} />
                  {seats.map((seat, index) => {
                    const selected = isSelected(seat.name);
                    return (
                      <IconCTA
                        key={`button-${index}`}
                        className={classNames(styles['seat-button'], { [styles['selected']]: isSelected })}
                        icon={{ jsx: <span>{seat.name}</span> }}
                        style={{
                          top: seat.top,
                          left: seat.left,
                          width: seat.width,
                          height: seat.height,
                        }}
                        disabled={type === ChecksSplitBySeatType.CreateChecks}
                        selected={selected}
                        onClick={() => onSelect(seat.name)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
            {Boolean(copy.info.length) && (
              <Flex horizontal gap="xs" alignItems="center">
                <FontIcon name="info" color="info" />
                <Copy value={copy.info} />
              </Flex>
            )}
          </Flex>
        </ModalContent>

        <ModalActions>
          <ModalAction color="secondary" label={copy.cancel} onClick={modal.close} />
          <ModalAction color="primary" label={copy.confirm} onClick={modal.onDone} />
        </ModalActions>

      </Modal>
    </FormProvider>
  );
};

export default ChecksSplitBySeat;
export type { ChecksSplitBySeatProps };
export { ChecksSplitBySeatType, sectionId };
